import { DatePicker } from '@/design-system/components/date-picker';
import { TaskContractSelect } from '@/features/finance/client-finance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  Check,
  Ellipsis,
  GitBranch,
  Link2,
  ListChecks,
  Plus,
  Timer,
  Trash2,
  X,
} from 'lucide-react';
import { useTimerActions } from '@/lib/time';
import { useCan } from '@/lib/session';
import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DONE_CATEGORIES, PRIORITIES, PRIORITY_LABELS, type RichText, type TaskDto } from '@cw/shared';
import { Badge } from '@/design-system/components/badge';
import { Button } from '@/design-system/components/button';
import { Input } from '@/design-system/components/input';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@/design-system/components/menu';
import { PeoplePicker } from '@/design-system/components/people-picker';
import { RichEditor } from '@/design-system/components/rich-text';
import { Select } from '@/design-system/components/select';
import { Sheet } from '@/design-system/components/sheet';
import { Skeleton } from '@/design-system/components/skeleton';
import { ErrorState } from '@/design-system/components/states';
import { TagPicker } from '@/design-system/components/tag-picker';
import { CommentThread } from '@/features/comments/comment-thread';
import { FilesPanel } from '@/features/files/files-panel';
import { PRIORITY_TONE } from '@/features/projects/meta';
import { api } from '@/lib/api';
import { handleFormError } from '@/lib/forms';
import { formatMoney, projectKeys } from '@/lib/projects';
import { useMe } from '@/lib/session';
import {
  dateInputFrom,
  dueFromDateInput,
  taskKeys,
  useServices,
  useTask,
  useTaskFiles,
  useTasks,
  useWorkflow,
} from '@/lib/tasks';
import { cn } from '@/lib/utils';
import { TaskDue } from './due';

/** Abre a tarefa indicada em `?task=` por cima de qualquer tela (link direto). */
export function TaskDrawerHost() {
  const [params, setParams] = useSearchParams();
  const id = params.get('task');
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('task');
    setParams(next, { replace: true });
  };
  return (
    <Sheet
      open={Boolean(id)}
      onOpenChange={(o) => !o && close()}
      side="right"
      title="Tarefa"
      className="w-[min(640px,100vw)]"
    >
      {id && <TaskDetail key={id} id={id} onClose={close} />}
    </Sheet>
  );
}

export function useOpenTask() {
  const [params, setParams] = useSearchParams();
  return (id: string) => {
    const next = new URLSearchParams(params);
    next.set('task', id);
    setParams(next);
  };
}

function useTaskMutation(task: TaskDto) {
  const qc = useQueryClient();
  return (fn: () => Promise<TaskDto>, success?: string) =>
    fn()
      .then((d) => {
        qc.setQueryData(taskKeys.detail(task.id), d);
        qc.invalidateQueries({ queryKey: taskKeys.all });
        qc.invalidateQueries({ queryKey: projectKeys.all });
        qc.invalidateQueries({ queryKey: ['finance'] });
        if (success) toast.success(success);
        return d;
      })
      .catch((e) => {
        handleFormError(e);
        throw e;
      });
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-h-9 grid-cols-[132px_1fr] items-center gap-2">
      <span className="text-[12.5px] text-fg-subtle">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-line px-5 py-5 md:px-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-[13.5px] font-semibold [&_svg]:size-4 [&_svg]:text-fg-subtle">
          {icon}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function TaskDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const task = useTask(id);
  if (task.isPending)
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40" />
      </div>
    );
  if (task.isError)
    return (
      <div className="p-6">
        <ErrorState error={task.error} onRetry={() => task.refetch()} />
      </div>
    );
  return <TaskBody task={task.data} onClose={onClose} />;
}

function TaskBody({ task: t, onClose }: { task: TaskDto; onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const openTask = useOpenTask();
  const run = useTaskMutation(t);
  const workflow = useWorkflow(t.project?.id ?? null);
  const editable = t.can.update;
  const [title, setTitle] = useState(t.title);
  const patch = (body: object, msg?: string) => run(() => api.patch<TaskDto>(`/tasks/${t.id}`, body), msg);
  const done = (DONE_CATEGORIES as readonly string[]).includes(t.status.category);
  const doneStatus = workflow.data?.statuses.find((s) => s.category === 'done');
  const firstStatus = workflow.data?.statuses[0];

  const canMoney = useCan('finance:read') && !me.role.startsWith('client_');
  const canTrack = useCan('time:track');
  const timer = useTimerActions();
  const remove = useMutation({
    mutationFn: () => api.del(`/tasks/${t.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: projectKeys.all });
      qc.invalidateQueries({ queryKey: ['finance'] });
      toast.success('Tarefa excluída');
      onClose();
    },
    onError: (e) => handleFormError(e),
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Cabeçalho */}
      <header className="flex items-center gap-2 border-b border-line px-5 py-3 md:px-6">
        <Menu>
          <MenuTrigger asChild disabled={!editable}>
            <button type="button" className="rounded-full disabled:cursor-default" aria-label="Mudar etapa">
              <Badge tone={t.status.color} dot className="h-7 px-3 text-[12.5px]">
                {t.status.name}
              </Badge>
            </button>
          </MenuTrigger>
          <MenuContent align="start">
            <MenuRadioGroup
              value={t.status.id}
              onValueChange={(statusId) =>
                run(() =>
                  api
                    .post(`/tasks/${t.id}/move`, { statusId })
                    .then(() => api.get<TaskDto>(`/tasks/${t.id}`)),
                )
              }
            >
              {workflow.data?.statuses.map((s) => (
                <MenuRadioItem key={s.id} value={s.id}>
                  <span
                    className="size-2 rounded-full"
                    style={{ background: `var(--color-label-${s.color})` }}
                  />
                  {s.name}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuContent>
        </Menu>
        {editable && doneStatus && !done && (
          <Button
            size="sm"
            onClick={() =>
              run(
                () =>
                  api
                    .post(`/tasks/${t.id}/move`, { statusId: doneStatus.id })
                    .then(() => api.get<TaskDto>(`/tasks/${t.id}`)),
                'Tarefa entregue',
              )
            }
          >
            <Check /> Marcar como entregue
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1">
          {canTrack && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => timer.start.mutate({ taskId: t.id })}
              loading={timer.start.isPending}
            >
              <Timer /> Timer
            </Button>
          )}
          {t.can.delete && (
            <Menu>
              <MenuTrigger asChild>
                <Button size="icon-sm" variant="ghost" aria-label="Mais ações">
                  <Ellipsis />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem
                  icon={<Link2 />}
                  onSelect={async () => {
                    await navigator.clipboard.writeText(
                      `${window.location.origin}${window.location.pathname}?task=${t.id}`,
                    );
                    toast.success('Link copiado');
                  }}
                >
                  Copiar link
                </MenuItem>
                <MenuItem icon={<Trash2 />} danger onSelect={() => remove.mutate()}>
                  Excluir tarefa
                </MenuItem>
              </MenuContent>
            </Menu>
          )}
          <Button size="icon-sm" variant="ghost" aria-label="Fechar" onClick={onClose}>
            <X />
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="px-5 pt-5 pb-4 md:px-6">
          {t.project && (
            <Link
              to={`/projects/p/${t.project.id}/tasks`}
              className="mb-2 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
            >
              <span
                className="size-2 rounded-[3px]"
                style={{ background: `var(--color-label-${t.project.color})` }}
              />
              {t.project.name}
              {t.client && <span className="text-fg-subtle">{t.client.name}</span>}
            </Link>
          )}
          <textarea
            value={title}
            disabled={!editable}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== t.title && patch({ title: title.trim() })}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), e.currentTarget.blur())}
            rows={1}
            className={cn(
              'field-sizing-content w-full resize-none bg-transparent text-[22px] leading-snug font-semibold tracking-[-0.02em] outline-none',
              done && 'text-fg-muted line-through',
            )}
            aria-label="Título"
          />
          {t.parentTaskId && (
            <button
              type="button"
              onClick={() => openTask(t.parentTaskId!)}
              className="mt-1 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
            >
              <GitBranch className="size-3.5" /> Subtarefa de outra demanda
            </button>
          )}

          <div className="mt-4 flex flex-col">
            <Row label="Responsáveis">
              <PeoplePicker
                value={t.responsibles}
                disabled={!editable}
                onChange={(ids) =>
                  run(() =>
                    api.put<TaskDto>(`/tasks/${t.id}/assignees`, {
                      responsibleIds: ids,
                      collaboratorIds: t.collaborators.map((c) => c.id),
                    }),
                  )
                }
              />
            </Row>
            <Row label="Colaboradores">
              <PeoplePicker
                value={t.collaborators}
                disabled={!editable}
                onChange={(ids) =>
                  run(() =>
                    api.put<TaskDto>(`/tasks/${t.id}/assignees`, {
                      responsibleIds: t.responsibles.map((r) => r.id),
                      collaboratorIds: ids,
                    }),
                  )
                }
              />
            </Row>
            <Row label="Prioridade">
              <Menu>
                <MenuTrigger asChild disabled={!editable}>
                  <button type="button" className="rounded-full">
                    <Badge tone={PRIORITY_TONE[t.priority]}>{PRIORITY_LABELS[t.priority]}</Badge>
                  </button>
                </MenuTrigger>
                <MenuContent align="start">
                  <MenuRadioGroup value={t.priority} onValueChange={(priority) => patch({ priority })}>
                    {PRIORITIES.map((p) => (
                      <MenuRadioItem key={p} value={p}>
                        {PRIORITY_LABELS[p]}
                      </MenuRadioItem>
                    ))}
                  </MenuRadioGroup>
                </MenuContent>
              </Menu>
            </Row>
            <Row label="Início">
              <DateField
                label="Início"
                value={t.startAt}
                disabled={!editable}
                onChange={(v) => patch({ startAt: v })}
              />
            </Row>
            <Row label="Prazo interno">
              <div className="flex items-center gap-2">
                <DateField
                  label="Prazo interno"
                  value={t.internalDueAt}
                  disabled={!editable}
                  onChange={(v) => patch({ internalDueAt: v })}
                />
                <TaskDue iso={t.internalDueAt} done={done} icon={false} />
              </div>
            </Row>
            <Row label="Prazo do cliente">
              <div className="flex items-center gap-2">
                <DateField
                  label="Prazo do cliente"
                  value={t.clientDueAt}
                  disabled={!editable}
                  onChange={(v) => patch({ clientDueAt: v })}
                />
                <TaskDue iso={t.clientDueAt} done={done} icon={false} />
              </div>
            </Row>
            <Row label="Estimativa">
              <HoursField
                minutes={t.estimateMinutes}
                disabled={!editable}
                onChange={(m) => patch({ estimateMinutes: m })}
              />
            </Row>
            {t.valueCents !== null || me.permissions.includes('finance:read') ? (
              <Row label="Valor">
                <MoneyField
                  cents={t.valueCents}
                  currency={me.organization.currency}
                  disabled={!editable}
                  onChange={(c) => patch({ valueCents: c })}
                />
              </Row>
            ) : null}
            <Row label="Tags">
              <div className="max-w-sm">
                <TagPicker value={t.tags.map((x) => x.id)} onChange={(tagIds) => patch({ tagIds })} />
              </div>
            </Row>
          </div>
        </div>

        {t.client && canMoney && (
          <TaskContractSelect
            clientId={t.client.id}
            value={t.contractId}
            onChange={(contractId) => patch({ contractId })}
          />
        )}
        <Materials task={t} run={run} editable={editable} />

        <Section title="Descrição">
          <RichEditor
            key={t.id}
            value={t.description}
            editable={editable}
            minHeight={90}
            placeholder="Detalhes, formatos, textos, referências…"
            onSave={(doc: RichText) => patch({ description: doc })}
          />
        </Section>

        <Subtasks
          task={t}
          editable={editable}
          firstStatusId={firstStatus?.id}
          doneStatusId={doneStatus?.id}
        />
        <Checklist task={t} run={run} editable={editable} />
        <Dependencies task={t} run={run} editable={editable} />
        <Links task={t} run={run} editable={editable} />

        <Section title="Anexos">
          <TaskAttachments task={t} />
        </Section>

        <Section title="Inbox">
          <CommentThread
            entityType="task"
            entityId={t.id}
            onChanged={() => qc.invalidateQueries({ queryKey: taskKeys.all })}
          />
        </Section>

        <p className="px-6 pb-8 text-[12px] text-fg-subtle">
          Criada {t.createdBy ? `por ${t.createdBy} ` : ''}em{' '}
          {new Date(t.createdAt).toLocaleString('pt-BR', { dateStyle: 'medium', timeStyle: 'short' })}
          {t.sourceRequestId && ' a partir de uma solicitação da Inbox'}
        </p>
      </div>
    </div>
  );
}

// Campos ---------------------------------------------------------------------

function DateField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string | null;
  onChange: (iso: string | null) => void;
  disabled?: boolean;
}) {
  return (
    <DatePicker
      aria-label={label}
      disabled={disabled}
      value={dateInputFrom(value)}
      onValueChange={(v) => onChange(dueFromDateInput(v))}
      className="h-8 rounded-[8px] bg-transparent px-1.5 text-[13px] [color-scheme:dark] hover:bg-hover focus-visible:bg-hover disabled:opacity-60"
    />
  );
}

function HoursField({
  minutes,
  onChange,
  disabled,
}: {
  minutes: number | null;
  onChange: (m: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px]">
      <input
        type="number"
        min={0}
        step={0.5}
        disabled={disabled}
        defaultValue={minutes ? minutes / 60 : ''}
        placeholder="—"
        onBlur={(e) => {
          const h = e.target.value === '' ? null : Math.round(Number(e.target.value) * 60);
          if (h !== minutes) onChange(h);
        }}
        className="tabular h-8 w-20 rounded-[8px] bg-transparent px-1.5 hover:bg-hover focus-visible:bg-hover"
        aria-label="Estimativa em horas"
      />
      <span className="text-fg-subtle">horas</span>
    </span>
  );
}

function MoneyField({
  cents,
  currency,
  onChange,
  disabled,
}: {
  cents: number | null;
  currency: string;
  onChange: (c: number | null) => void;
  disabled?: boolean;
}) {
  return (
    <input
      inputMode="decimal"
      disabled={disabled}
      defaultValue={cents !== null ? (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}
      placeholder={`Sem valor (${currency})`}
      onBlur={(e) => {
        const raw = e.target.value.replace(/[^\d,.-]/g, '');
        const n = raw
          ? Math.round(Number(raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw) * 100)
          : null;
        if (n !== cents && (n === null || Number.isFinite(n))) onChange(n);
      }}
      className="tabular h-8 w-36 rounded-[8px] bg-transparent px-1.5 text-[13px] hover:bg-hover focus-visible:bg-hover"
      aria-label="Valor da tarefa"
    />
  );
}

// Seções -------------------------------------------------------------------------

type Run = ReturnType<typeof useTaskMutation>;

function Materials({ task: t, run, editable }: { task: TaskDto; run: Run; editable: boolean }) {
  const services = useServices();
  const [pick, setPick] = useState('');
  const options = (services.data ?? []).flatMap((c) =>
    c.types.filter((x) => x.isActive).map((x) => ({ value: x.id, label: x.name, hint: c.name })),
  );
  const total = t.items.reduce((sum, i) => sum + (i.unitPriceCents ?? 0) * i.quantity, 0);
  const showMoney = t.items.some((i) => i.unitPriceCents !== null);
  return (
    <Section title="Materiais">
      {t.items.length > 0 && (
        <ul className="mb-3 divide-y divide-line rounded-panel shadow-[inset_0_0_0_1px_var(--color-line)]">
          {t.items.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-3 py-2">
              <input
                type="number"
                min={1}
                disabled={!editable}
                defaultValue={i.quantity}
                onBlur={(e) => {
                  const q = Math.max(1, Number(e.target.value) || 1);
                  if (q !== i.quantity)
                    run(() => api.patch<TaskDto>(`/tasks/${t.id}/items/${i.id}`, { quantity: q }));
                }}
                className="tabular h-8 w-14 rounded-[8px] bg-canvas/50 text-center text-[13px]"
                aria-label={`Quantidade de ${i.name}`}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px]">{i.name}</p>
                <p className="text-[11.5px] text-fg-subtle">{i.categoryName}</p>
              </div>
              {i.unitPriceCents !== null && (
                <span className="tabular text-[12.5px] text-fg-muted">
                  {formatMoney(i.unitPriceCents * i.quantity)}
                </span>
              )}
              {editable && (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Remover ${i.name}`}
                  onClick={() => run(() => api.del<TaskDto>(`/tasks/${t.id}/items/${i.id}`))}
                >
                  <X />
                </Button>
              )}
            </li>
          ))}
          {showMoney && (
            <li className="flex justify-between px-3 py-2 text-[12.5px]">
              <span className="text-fg-subtle">Total pela tabela de preços</span>
              <span className="tabular font-medium">{formatMoney(total)}</span>
            </li>
          )}
        </ul>
      )}
      {editable && (
        <div className="flex gap-2">
          <Select
            className="h-9 flex-1"
            value={pick || undefined}
            onValueChange={setPick}
            options={options}
            placeholder="Adicionar material (Feed, Reel, Logo…)"
          />
          <Button
            size="md"
            disabled={!pick}
            onClick={() =>
              run(() => api.post<TaskDto>(`/tasks/${t.id}/items`, { serviceTypeId: pick, quantity: 1 })).then(
                () => setPick(''),
              )
            }
          >
            <Plus /> Adicionar
          </Button>
        </div>
      )}
      {!editable && t.items.length === 0 && (
        <p className="text-[13px] text-fg-subtle">Nenhum material definido.</p>
      )}
    </Section>
  );
}

function Subtasks({
  task: t,
  editable,
  firstStatusId,
  doneStatusId,
}: {
  task: TaskDto;
  editable: boolean;
  firstStatusId?: string | undefined;
  doneStatusId?: string | undefined;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: taskKeys.detail(t.id) });
    qc.invalidateQueries({ queryKey: taskKeys.all });
  };
  const add = useMutation({
    mutationFn: () => api.post('/tasks', { title: title.trim(), parentTaskId: t.id }),
    onSuccess: () => (setTitle(''), refresh()),
    onError: (e) => handleFormError(e),
  });
  const toggle = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.post(`/tasks/${id}/move`, { statusId: done ? doneStatusId : firstStatusId }),
    onSuccess: refresh,
    onError: (e) => handleFormError(e),
  });
  if (t.parentTaskId) return null;
  return (
    <Section
      title="Subtarefas"
      icon={<GitBranch />}
      action={
        t.subtaskCount > 0 && (
          <span className="tabular text-[12px] text-fg-subtle">
            {t.subtaskDone}/{t.subtaskCount}
          </span>
        )
      }
    >
      <ul className="flex flex-col gap-0.5">
        {t.subtasks.map((s) => {
          const done = Boolean(s.completedAt);
          return (
            <li
              key={s.id}
              className="group -mx-2 flex items-center gap-2.5 rounded-[8px] px-2 py-1.5 hover:bg-hover"
            >
              <CheckCircle
                checked={done}
                disabled={!editable || !doneStatusId}
                onChange={(v) => toggle.mutate({ id: s.id, done: v })}
                label={s.title}
              />
              <Link
                to={`?task=${s.id}`}
                className={cn('min-w-0 flex-1 truncate text-[13.5px]', done && 'text-fg-muted line-through')}
              >
                {s.title}
              </Link>
              {s.responsibles[0] && (
                <span className="text-[12px] text-fg-subtle">{s.responsibles[0].name.split(' ')[0]}</span>
              )}
              <TaskDue iso={s.internalDueAt} done={done} />
              <ArrowUpRight className="size-3.5 text-fg-subtle opacity-0 group-hover:opacity-100" />
            </li>
          );
        })}
      </ul>
      {editable && (
        <form
          onSubmit={(e) => (e.preventDefault(), title.trim() && add.mutate())}
          className="mt-2 flex gap-2"
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nova subtarefa"
            className="h-9"
          />
          <Button type="submit" disabled={!title.trim()} loading={add.isPending}>
            Adicionar
          </Button>
        </form>
      )}
    </Section>
  );
}

export function CheckCircle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => (e.stopPropagation(), onChange(!checked))}
      className={cn(
        'flex size-[18px] shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-default',
        checked
          ? 'bg-signal text-signal-fg'
          : 'shadow-[inset_0_0_0_1.5px_var(--color-line-strong)] hover:shadow-[inset_0_0_0_1.5px_var(--color-fg-muted)]',
      )}
    >
      {checked && <Check className="size-3" strokeWidth={3} />}
    </button>
  );
}

function Checklist({ task: t, run, editable }: { task: TaskDto; run: Run; editable: boolean }) {
  const [text, setText] = useState('');
  return (
    <Section
      title="Checklist"
      icon={<ListChecks />}
      action={
        t.checklistCount > 0 && (
          <span className="tabular text-[12px] text-fg-subtle">
            {t.checklistDone}/{t.checklistCount}
          </span>
        )
      }
    >
      <ul className="flex flex-col gap-0.5">
        {t.checklist.map((c) => (
          <li
            key={c.id}
            className="group -mx-2 flex items-center gap-2.5 rounded-[8px] px-2 py-1.5 hover:bg-hover"
          >
            <CheckCircle
              checked={c.done}
              disabled={!editable}
              label={c.text}
              onChange={(done) => run(() => api.patch<TaskDto>(`/tasks/${t.id}/checklist/${c.id}`, { done }))}
            />
            <span className={cn('flex-1 text-[13.5px]', c.done && 'text-fg-muted line-through')}>
              {c.text}
            </span>
            {editable && (
              <button
                type="button"
                aria-label={`Remover ${c.text}`}
                onClick={() => run(() => api.del<TaskDto>(`/tasks/${t.id}/checklist/${c.id}`))}
                className="text-fg-subtle opacity-0 group-hover:opacity-100 hover:text-danger max-md:opacity-100"
              >
                <X className="size-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <form
          onSubmit={(e) => (
            e.preventDefault(),
            text.trim() &&
              run(() => api.post<TaskDto>(`/tasks/${t.id}/checklist`, { text: text.trim() })).then(() =>
                setText(''),
              )
          )}
          className="mt-2 flex gap-2"
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Novo item (ex.: revisar ortografia)"
            className="h-9"
          />
          <Button type="submit" disabled={!text.trim()}>
            Adicionar
          </Button>
        </form>
      )}
    </Section>
  );
}

function Dependencies({ task: t, run, editable }: { task: TaskDto; run: Run; editable: boolean }) {
  const siblings = useTasks({ projectId: t.project?.id, parentId: 'none' }, Boolean(t.project) && editable);
  const [pick, setPick] = useState('');
  const options = (siblings.data ?? [])
    .filter((s) => s.id !== t.id && !t.dependencies.some((d) => d.id === s.id))
    .map((s) => ({ value: s.id, label: s.title }));
  if (!t.project && !t.dependencies.length) return null;
  return (
    <Section title="Depende de">
      {t.dependencies.length > 0 ? (
        <ul className="mb-2 flex flex-col gap-1">
          {t.dependencies.map((d) => (
            <li key={d.id} className="flex items-center gap-2 text-[13.5px]">
              <span className={cn('size-2 rounded-full', d.done ? 'bg-success' : 'bg-warning')} />
              <Link to={`?task=${d.id}`} className="flex-1 truncate hover:underline">
                {d.title}
              </Link>
              <span className="text-[12px] text-fg-subtle">{d.done ? 'concluída' : 'pendente'}</span>
              {editable && (
                <button
                  type="button"
                  aria-label="Remover dependência"
                  onClick={() => run(() => api.del<TaskDto>(`/tasks/${t.id}/dependencies/${d.id}`))}
                  className="text-fg-subtle hover:text-danger"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-[13px] text-fg-subtle">Nenhuma dependência.</p>
      )}
      {editable && options.length > 0 && (
        <div className="flex gap-2">
          <Select
            className="h-9 flex-1"
            value={pick || undefined}
            onValueChange={setPick}
            options={options}
            placeholder="Esta tarefa depende de…"
          />
          <Button
            disabled={!pick}
            onClick={() =>
              run(() => api.post<TaskDto>(`/tasks/${t.id}/dependencies`, { dependsOnTaskId: pick })).then(
                () => setPick(''),
              )
            }
          >
            Adicionar
          </Button>
        </div>
      )}
    </Section>
  );
}

function Links({ task: t, run, editable }: { task: TaskDto; run: Run; editable: boolean }) {
  const [url, setUrl] = useState('');
  return (
    <Section title="Links" icon={<Link2 />}>
      {t.links.length > 0 && (
        <ul className="mb-2 flex flex-col gap-1">
          {t.links.map((l) => (
            <li key={l.id} className="flex items-center gap-2 text-[13.5px]">
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate hover:underline"
              >
                {l.title || l.url}
              </a>
              {editable && (
                <button
                  type="button"
                  aria-label="Remover link"
                  onClick={() => run(() => api.del<TaskDto>(`/tasks/${t.id}/links/${l.id}`))}
                  className="text-fg-subtle hover:text-danger"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <form
          onSubmit={(e) => (
            e.preventDefault(),
            url.trim() &&
              run(() => api.post<TaskDto>(`/tasks/${t.id}/links`, { url: url.trim() })).then(() => setUrl(''))
          )}
          className="flex gap-2"
        >
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://figma.com/…"
            className="h-9"
            inputMode="url"
          />
          <Button type="submit" disabled={!url.trim()}>
            Adicionar
          </Button>
        </form>
      )}
      {!editable && !t.links.length && <p className="text-[13px] text-fg-subtle">Nenhum link.</p>}
    </Section>
  );
}

function TaskAttachments({ task: t }: { task: TaskDto }) {
  return (
    <FilesPanel
      target={{ purpose: 'task_attachment', taskId: t.id }}
      files={useTaskFiles(t.id)}
      invalidate={[taskKeys.files(t.id), taskKeys.detail(t.id), taskKeys.all]}
      canUpload={t.can.update}
    />
  );
}
